import { useEffect, useRef, useState, useCallback } from "react";
import type { UploadUpdate } from "@/types/api";

interface UseUploadSocketOptions {
  clientId: string | null;
  path?: string;
  onUpdate?: (update: UploadUpdate) => void;
}

interface UseUploadSocketResult {
  connected: boolean;
  lastUpdate: UploadUpdate | null;
}

const MAX_RETRY_DELAY = 15_000;
const BASE_RETRY_DELAY = 1_000;

export const useUploadSocket = ({
  clientId,
  path = "/ws",
  onUpdate,
}: UseUploadSocketOptions): UseUploadSocketResult => {
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<UploadUpdate | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef<number>(BASE_RETRY_DELAY);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onUpdate);

  useEffect(() => {
    callbackRef.current = onUpdate;
  }, [onUpdate]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const parsed = JSON.parse(event.data) as
        | UploadUpdate
        | UploadUpdate[];
      const items = Array.isArray(parsed) ? parsed : [parsed];
      for (const item of items) {
        setLastUpdate(item);
        callbackRef.current?.(item);
      }
    } catch {
      // ignore malformed payload
    }
  }, []);

  useEffect(() => {
    if (!clientId) return;

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}${path}/${clientId}`;
      const socket = new WebSocket(url);
      wsRef.current = socket;

      socket.onopen = () => {
        retryRef.current = BASE_RETRY_DELAY;
        setConnected(true);
      };
      socket.onclose = () => {
        setConnected(false);
        if (cancelled) return;
        const delay = retryRef.current;
        retryRef.current = Math.min(delay * 2, MAX_RETRY_DELAY);
        timerRef.current = setTimeout(connect, delay);
      };
      socket.onerror = () => socket.close();
      socket.onmessage = handleMessage;
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [clientId, path, handleMessage]);

  return { connected, lastUpdate };
};
